import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ArrowLeft, Save, CheckCircle2, Loader2, UploadCloud, FileText, Trash2, Download, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI, DOC_STATUS_META } from '../../lib/onboardingAPI';

const fmtSize = (b) => (b == null ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

function StatusBadge({ status }) {
  const m = DOC_STATUS_META[status] || DOC_STATUS_META.optional;
  return <Badge className={m.color}>{m.label}</Badge>;
}

function ChecklistItem({ item, onUpload, onDelete, onDownload, busy }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length) onUpload(item, files);
  };

  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid={`doc-item-${item.key}`}>
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-foreground">{item.label}</span>
          {item.required && <span className="ml-2 text-xs text-red-500">required</span>}
        </div>
        <StatusBadge status={item.item_status} />
      </div>

      {/* Uploaded files */}
      {item.files.length > 0 && (
        <div className="space-y-1.5">
          {item.files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-sm bg-muted/50 rounded-md p-2">
              <FileText className="w-4 h-4 text-muted-foreground/70 flex-shrink-0" />
              <span className="flex-1 truncate text-foreground/90">{f.filename}</span>
              <span className="text-xs text-muted-foreground/70">{fmtSize(f.size_bytes)}</span>
              <StatusBadge status={f.status} />
              <Button size="sm" variant="ghost" title="Download" onClick={() => onDownload(f)}><Download className="w-4 h-4 text-muted-foreground" /></Button>
              <Button size="sm" variant="ghost" title="Delete" onClick={() => onDelete(f.id)} disabled={busy}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition ${dragging ? 'border-primary bg-accent' : 'border-border hover:border-border'}`}
        data-testid={`doc-drop-${item.key}`}
      >
        <UploadCloud className="w-5 h-5 mx-auto text-muted-foreground/70 mb-1" />
        <p className="text-xs text-muted-foreground">
          {item.files.length ? 'Drag a new file to replace, or upload another' : 'Drag & drop or click to upload'}
        </p>
        <input ref={inputRef} type="file" multiple className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>
    </div>
  );
}

export default function DocumentCentreForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await onboardingAPI.documentCentre(clientId);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const uploadFiles = async (item, files) => {
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('doc_key', item.key);
        if (item.category) fd.append('category', item.category);
        await onboardingAPI.uploadDocument(clientId, fd);
      }
      toast.success(files.length > 1 ? `${files.length} files uploaded` : 'File uploaded');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const deleteFile = async (docId) => {
    setBusy(true);
    try { await onboardingAPI.deleteDocument(clientId, docId); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };

  const downloadFile = async (f) => {
    try {
      const res = await onboardingAPI.downloadDocument(f.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: f.mime_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url; a.download = f.filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download file'); }
  };

  const finalize = async (status) => {
    setBusy(true);
    try {
      await onboardingAPI.finalizeDocuments(clientId, { status });
      toast.success(status === 'completed' ? 'Documents section completed' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  if (!data) return null;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Document Upload Centre</CardTitle>
          <CardDescription>
            These documents are requested based on your onboarding answers. Upload securely — you can replace or delete files any time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.required_missing?.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{data.required_missing.length} required document(s) still missing. Please upload them when you can.</span>
            </div>
          )}

          {data.items.map((item) => (
            <ChecklistItem
              key={item.key} item={item} busy={busy}
              onUpload={uploadFiles} onDelete={deleteFile} onDownload={downloadFile}
            />
          ))}

          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => finalize('in_progress')} disabled={busy} data-testid="doc-save">
              <Save className="w-4 h-4 mr-2" /> Save &amp; return later
            </Button>
            <Button onClick={() => finalize('completed')} disabled={busy} data-testid="doc-complete">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
